<template>
  <Teleport :to="options?.teleportTarget ?? 'body'">
    <WdsModal
      :show="isRevealed"
      :title="options?.actionName ?? options?.title"
      :sm="modalSize === 'sm'"
      :md="modalSize === 'md'"
      :lg="modalSize === 'lg'"
      @cancel="handleCancel"
    >
      <div
        ref="contentRef"
        class="tw-flex tw-flex-col tw-justify-center"
        :style="contentMinHeightStyle"
      >
        <WdsStack
          v-if="isLoading && options?.loadingTitle"
          justify-center
          align-center
          direction-column
          gap-md
          class="content"
        >
          <WdsSpinner indeterminate />
          <WdsText large>{{ options.loadingTitle }}</WdsText>
        </WdsStack>
        <WdsStack v-else justify-center align-center direction-column gap-md class="content">
          <div class="icon-outer" :style="{ backgroundColor: preset.bgOuter }">
            <div class="icon-inner" :style="{ backgroundColor: preset.bgInner }">
              <WdsIcon
                :name="options?.icon ?? preset.icon"
                :color="options?.iconColor ?? preset.color"
                :size="24"
              />
            </div>
          </div>

          <component
            :is="options.component"
            v-if="options?.component"
            v-bind="options.componentProps"
          />
          <template v-else>
            <WdsText large>{{ options?.title }}</WdsText>
            <WdsText v-if="options?.description" secondary>
              <BaseHtml :html="options?.description" />
            </WdsText>
            <WdsText v-if="options?.note" small secondary>{{ options.note }}</WdsText>
          </template>

          <WdsStack
            :justify-space-between="!options?.hideCancel"
            :justify-end="!!options?.hideCancel"
            align-center
            class="tw-w-full"
          >
            <WdsButton v-if="!options?.hideCancel" :disabled="isLoading" @click="handleCancel">
              {{ options?.cancelText ?? $t('Cancel') }}
            </WdsButton>
            <WdsButton
              :disabled="isLoading"
              v-bind="confirmButtonColorProps"
              data-test="confirm-modal-confirm-button"
              @click.prevent="handleConfirm"
            >
              {{ options?.confirmText ?? $t('Confirm') }}
              <WdsSpinner v-if="isLoading" indeterminate class="tw-m-0.5" />
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </div>
    </WdsModal>
  </Teleport>
</template>

<script setup lang="ts">
import {
  WdsText,
  WdsStack,
  WdsIcon,
  WdsModal,
  WdsButton,
  WdsSpinner,
} from '@worksome/wds-components'
import { computed, nextTick, ref, watch } from 'vue'
import BaseHtml from '@/components/BaseHtml.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useConfirmModal } from '@/composables/useConfirmModal'
import type {
  ConfirmModalButtonVariant,
  ConfirmModalIcon,
  ConfirmModalTemplate,
} from '@/composables/useConfirmModal'

const { isRevealed, options, isLoading, handleConfirm, handleCancel } = useConfirmModal()

const templatePresets: Record<
  ConfirmModalTemplate,
  {
    icon: ConfirmModalIcon
    color: string
    bgOuter: string
    bgInner: string
    buttonVariant: ConfirmModalButtonVariant
  }
> = {
  danger: {
    icon: 'trash-can',
    color: '#D12223',
    bgOuter: '#FDF2F2',
    bgInner: '#F9E0E0',
    buttonVariant: 'error',
  },
  warning: {
    icon: 'warning-triangle',
    color: '#B54708',
    bgOuter: '#FFFAEB',
    bgInner: '#FEF0C7',
    buttonVariant: 'warning',
  },
  confirmation: {
    icon: 'checkmark-outline',
    color: '#087443',
    bgOuter: '#ECFDF3',
    bgInner: '#D1FADF',
    buttonVariant: 'primary',
  },
  info: {
    icon: 'info',
    color: '#175CD3',
    bgOuter: '#EFF8FF',
    bgInner: '#D1E9FF',
    buttonVariant: 'info',
  },
  delete: {
    icon: 'trash-can',
    color: '#D12223',
    bgOuter: '#FDF2F2',
    bgInner: '#F9E0E0',
    buttonVariant: 'delete',
  },
}

// WdsButton has no 'delete' prop of its own, so our 'delete' variant maps to its 'error' styling.
const wdsButtonPropByVariant: Record<ConfirmModalButtonVariant, string> = {
  primary: 'primary',
  secondary: 'secondary',
  error: 'error',
  delete: 'error',
  warning: 'warning',
  success: 'success',
  info: 'info',
}

const modalSize = computed(() => options.value?.size ?? 'md')
const preset = computed(() => templatePresets[options.value?.template ?? 'confirmation'])
const confirmButtonColorProps = computed(() => ({
  [wdsButtonPropByVariant[options.value?.confirmVariant ?? preset.value.buttonVariant]]: true,
}))

const contentRef = ref<HTMLElement>()
const contentMinHeight = ref<number>()
const contentMinHeightStyle = computed(() =>
  contentMinHeight.value ? { minHeight: `${contentMinHeight.value}px` } : undefined,
)

watch(isRevealed, async (revealed) => {
  if (!revealed) {
    contentMinHeight.value = undefined
    return
  }

  await nextTick()
  contentMinHeight.value = contentRef.value?.offsetHeight
})
</script>

<style lang="scss" scoped>
.content {
  :deep(strong) {
    color: black;
  }

  .icon-outer {
    border-radius: 50%;
    padding: 6px;

    .icon-inner {
      padding: 5px;
      border-radius: 50%;
    }
  }
}
</style>
